import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  MarkdownContent,
  Page,
  SupportButton,
} from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
// docs/schema.md lives at the repo root, outside this workspace. Rather than
// import it across the package boundary as a bundler asset (which fails to
// resolve in the Heroku production build and would be stripped from the slug by
// `.slugignore`), its content is embedded as a string generated at build time
// from docs/schema.md. See scripts/generate-schema-content.mjs.
import { schemaMarkdown } from './schemaMarkdown.generated';

// MarkdownContent renders the doc as plain HTML with no styling of its own, so
// the reference read as an unformatted wall of text. These styles target the
// rendered elements (headings, tables, code, callouts) to give the page a
// readable, card-based layout that matches the rest of the app.
const useStyles = makeStyles(theme => ({
  card: {
    maxWidth: 1080,
    margin: '0 auto',
  },
  markdown: {
    '& h1': {
      fontSize: '1.6rem',
      marginTop: 0,
      marginBottom: theme.spacing(2),
    },
    '& h2': {
      fontSize: '1.3rem',
      marginTop: theme.spacing(4),
      paddingBottom: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& h3': {
      fontSize: '1.05rem',
      marginTop: theme.spacing(3),
    },
    '& p, & li': {
      lineHeight: 1.65,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
    // The RFC/kebab-case guidance renders as a leading blockquote — style it as
    // a callout so it reads as guidance rather than body copy.
    '& blockquote': {
      margin: theme.spacing(2, 0),
      padding: theme.spacing(1.5, 2),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
      '& p': { margin: 0 },
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: theme.spacing(2, 0),
      fontSize: '0.9rem',
    },
    '& th': {
      textAlign: 'left',
      padding: theme.spacing(1, 1.5),
      backgroundColor: theme.palette.background.default,
      borderBottom: `2px solid ${theme.palette.divider}`,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    '& td': {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'top',
    },
    '& tbody tr:nth-child(even)': {
      backgroundColor: theme.palette.action.hover,
    },
    '& code': {
      fontFamily: 'monospace',
      fontSize: '0.85em',
      padding: theme.spacing(0.25, 0.75),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.action.selected,
    },
    '& pre': {
      padding: theme.spacing(2),
      margin: theme.spacing(2, 0),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      overflowX: 'auto',
    },
    // Code inside a fenced block should not get the inline-code chip treatment.
    '& pre code': {
      padding: 0,
      backgroundColor: 'transparent',
    },
    // The `---` separators are redundant now that each h2 has an underline.
    '& hr': {
      display: 'none',
    },
  },
}));

export const SchemaPage = () => {
  const classes = useStyles();

  return (
    <Page themeId="documentation">
      <Header
        title="Catalog Schema"
        subtitle="Component catalog field reference (catalog-info.yaml)"
      />
      <Content>
        <ContentHeader title="">
          <SupportButton title="About this reference">
            The fields you can set on Component entities in catalog-info.yaml,
            based on Heroku RFC 000318. Heroku keys use kebab-case and their
            values are lowercase.
          </SupportButton>
        </ContentHeader>
        <InfoCard className={classes.card}>
          {/* dialect="gfm" so the GitHub-flavored tables in schema.md render as tables */}
          <div className={classes.markdown}>
            <MarkdownContent content={schemaMarkdown} dialect="gfm" />
          </div>
        </InfoCard>
      </Content>
    </Page>
  );
};
